import { api, query } from "./client";
import type {
  AccountSummary,
  AppCreateRequest,
  AppUpdateRequest,
  RegisteredApp,
  PerformanceReport,
  AdChain,
  AdChainEntryRequest,
  AdChainRequest,
  AdChainUpdateRequest,
  SourceDeviceBlockGroup,
  SourceDeviceBlockRequest,
  SourceDeviceBlock,
  Advertiser,
  AdvertiserGroup,
  AdvertiserRequest,
  AdvertiserStatus,
  AdvertiserStatusChange,
  Campaign,
  CampaignMetrics,
  CampaignRequest,
  CampaignStatus,
  Creative,
  CurrentUser,
  Page,
  TimeSeries,
} from "./types";

/**
 * Every admin endpoint the panel calls, in one place.
 *
 * <p>Grouped by resource so a screen imports what it needs and nothing else, and so a contract
 * change touches one file rather than every component that happened to know a URL.
 */

const ADMIN = "/api/v1/admin";

/**
 * The app registry (ADS-0043).
 *
 * `active` is what the campaign and chain forms offer; `list` includes retired
 * apps so their history stays reachable.
 */
export const appsApi = {
  list: () => api.get<RegisteredApp[]>(`${ADMIN}/apps`),
  active: () => api.get<RegisteredApp[]>(`${ADMIN}/apps/active`),
  create: (body: AppCreateRequest) =>
    api.post<RegisteredApp>(`${ADMIN}/apps`, body),
  update: (id: string, body: AppUpdateRequest) =>
    api.put<RegisteredApp>(`${ADMIN}/apps/${id}`, body),
};

export const authApi = {
  currentUser: () => api.get<CurrentUser>(`${ADMIN}/auth/me`),
};

export const advertisersApi = {
  list: (params: {
    search?: string;
    status?: AdvertiserStatus;
    page?: number;
    size?: number;
  }) =>
    api.get<Page<Advertiser>>(`${ADMIN}/advertisers${query({ ...params })}`),

  get: (id: string) => api.get<Advertiser>(`${ADMIN}/advertisers/${id}`),

  create: (body: AdvertiserRequest) =>
    api.post<Advertiser>(`${ADMIN}/advertisers`, body),

  update: (id: string, body: AdvertiserRequest) =>
    api.put<Advertiser>(`${ADMIN}/advertisers/${id}`, body),

  changeStatus: (id: string, status: AdvertiserStatus) =>
    api.patch<AdvertiserStatusChange>(
      `${ADMIN}/advertisers/${id}/status${query({ status })}`,
    ),

  // ADS-0047. Cascades to the advertiser's campaigns, and is refused outright if any of them has
  // history — the message names the campaign so the operator knows where to look.
  remove: (id: string) => api.delete<void>(`${ADMIN}/advertisers/${id}`),
};

export const campaignsApi = {
  list: (params: {
    advertiserId?: string;
    status?: CampaignStatus;
    page?: number;
    size?: number;
  }) => api.get<Page<Campaign>>(`${ADMIN}/campaigns${query({ ...params })}`),

  /**
   * The campaign list grouped by advertiser, then by placement (ADS-0048).
   *
   * `page` and `size` count **advertisers**, not campaigns — that is what makes each group arrive
   * whole. The flat `list` above stays for the callers that want a page of campaigns: the campaign
   * picker, and anything filtered to a single advertiser.
   */
  listGrouped: (params: { status?: CampaignStatus; page?: number; size?: number }) =>
    api.get<Page<AdvertiserGroup>>(
      `${ADMIN}/campaigns/grouped${query({ ...params })}`,
    ),

  get: (id: string) => api.get<Campaign>(`${ADMIN}/campaigns/${id}`),

  create: (body: CampaignRequest) =>
    api.post<Campaign>(`${ADMIN}/campaigns`, body),

  update: (id: string, body: CampaignRequest) =>
    api.put<Campaign>(`${ADMIN}/campaigns/${id}`, body),

  changeStatus: (id: string, status: CampaignStatus) =>
    api.patch<Campaign>(`${ADMIN}/campaigns/${id}/status`, { status }),

  // ADS-0047. Refused with CAMPAIGN_HAS_HISTORY once the campaign has any event or granted
  // reward; the server's message says what to do instead, so callers should surface it verbatim.
  remove: (id: string) => api.delete<void>(`${ADMIN}/campaigns/${id}`),

  metrics: (id: string, params: { from?: string; to?: string } = {}) =>
    api.get<CampaignMetrics>(
      `${ADMIN}/campaigns/${id}/metrics${query({ ...params })}`,
    ),

  timeSeries: (id: string, params: { from?: string; to?: string } = {}) =>
    api.get<TimeSeries>(
      `${ADMIN}/campaigns/${id}/timeseries${query({ ...params })}`,
    ),
};

export const creativesApi = {
  get: (campaignId: string) =>
    api.get<Creative>(`${ADMIN}/campaigns/${campaignId}/creative`),

  upload: (campaignId: string, file: File, clickUrl: string) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.upload<Creative>(
      `${ADMIN}/campaigns/${campaignId}/creative${query({ clickUrl })}`,
      formData,
    );
  },
};

export const reportsApi = {
  summary: (params: { from?: string; to?: string } = {}) =>
    api.get<AccountSummary>(`${ADMIN}/reports/summary${query({ ...params })}`),
};

/**
 * Per-source performance (ADS-0036).
 *
 * Diagnostics: which network actually fills each placement, so chain order is decided on evidence
 * rather than on a guess.
 */
export const sourcePerformanceApi = {
  get: (appId: string, days = 7) =>
    api.get<PerformanceReport>(
      `${ADMIN}/source-performance${query({ appId, days })}`,
    ),
};

/**
 * Mediation chains (ADS-0034).
 *
 * <p>Every mutation returns the whole chain, so callers replace their cached copy rather than
 * patching it. A chain is only meaningful as an ordered whole, and reassembling it here is how the
 * panel and the server would start to disagree about the order.
 */
export const adChainsApi = {
  list: (appId?: string) =>
    api.get<AdChain[]>(`${ADMIN}/ad-chains${query({ appId })}`),

  apps: () => api.get<string[]>(`${ADMIN}/ad-chains/apps`),

  get: (id: string) => api.get<AdChain>(`${ADMIN}/ad-chains/${id}`),

  create: (body: AdChainRequest) =>
    api.post<AdChain>(`${ADMIN}/ad-chains`, body),

  update: (id: string, body: AdChainUpdateRequest) =>
    api.put<AdChain>(`${ADMIN}/ad-chains/${id}`, body),

  remove: (id: string) => api.delete<void>(`${ADMIN}/ad-chains/${id}`),

  addEntry: (id: string, body: AdChainEntryRequest) =>
    api.post<AdChain>(`${ADMIN}/ad-chains/${id}/entries`, body),

  updateEntry: (id: string, entryId: string, body: AdChainEntryRequest) =>
    api.put<AdChain>(`${ADMIN}/ad-chains/${id}/entries/${entryId}`, body),

  removeEntry: (id: string, entryId: string) =>
    api.delete<AdChain>(`${ADMIN}/ad-chains/${id}/entries/${entryId}`),

  /** Send every entry id in the desired order; the server rejects a partial list. */
  reorder: (id: string, entryIds: string[]) =>
    api.put<AdChain>(`${ADMIN}/ad-chains/${id}/order`, { entryIds }),
};

/**
 * Installations on which one ad source is silenced (ADS-0052).
 *
 * <p>Exists so the operator's own handsets stop feeding AdMob the traffic it penalises, while the
 * same phones keep receiving our direct campaigns. A block takes effect on that device's next ad
 * load — the served document is marked uncacheable when it has been filtered.
 */
export const sourceDeviceBlocksApi = {
  list: () =>
    api.get<SourceDeviceBlockGroup[]>(`${ADMIN}/source-device-blocks`),

  create: (body: SourceDeviceBlockRequest) =>
    api.post<SourceDeviceBlock>(`${ADMIN}/source-device-blocks`, body),

  remove: (id: string) =>
    api.delete<void>(`${ADMIN}/source-device-blocks/${id}`),
};

/**
 * Query keys, centralised so an invalidation cannot miss a cache entry.
 *
 * <p>Scoped per resource: changing a campaign invalidates campaign lists and that campaign's
 * detail, and leaves advertiser data alone. A blunt "invalidate everything" would refetch the whole
 * panel on every edit.
 */
export const queryKeys = {
  currentUser: ["currentUser"] as const,

  apps: ["apps"] as const,
  activeApps: ["apps", "active"] as const,

  advertisers: (params?: unknown) => ["advertisers", params ?? {}] as const,
  advertiser: (id: string) => ["advertiser", id] as const,

  campaigns: (params?: unknown) => ["campaigns", params ?? {}] as const,
  // Deliberately under the "campaigns" prefix: every status mutation already invalidates
  // ["campaigns"], so the grouped list refreshes after a pause or a finish without a second
  // invalidation that someone would eventually forget to add.
  campaignsGrouped: (params?: unknown) =>
    ["campaigns", "grouped", params ?? {}] as const,
  campaign: (id: string) => ["campaign", id] as const,
  campaignMetrics: (id: string, range?: unknown) =>
    ["campaign", id, "metrics", range ?? {}] as const,
  campaignSeries: (id: string, range?: unknown) =>
    ["campaign", id, "series", range ?? {}] as const,

  creative: (campaignId: string) => ["creative", campaignId] as const,

  summary: (range?: unknown) => ["summary", range ?? {}] as const,

  adChains: (appId?: string) => ["adChains", appId ?? "all"] as const,
  adChainApps: ["adChainApps"] as const,
  sourceDeviceBlocks: ["sourceDeviceBlocks"] as const,
  sourcePerformance: (appId: string, days: number) =>
    ["sourcePerformance", appId, days] as const,
};
